import {
    liteGetDocument,
    liteQueryCollectionWith,
    liteCollections
} from "~/utils/firestoreLite"
import {
    VaccineDoseHistoryInput,
    OtherVaccinationHistoryInput
} from "~/utils/vaccineEligibility"
import { VaccineMenuSetting } from "@ambii-com/core"
import type { 
    VaccineMenuSettingSnapshot,
    ScheduledSeasonalVaccineDose
} from "~/services/VaccineAppointmentService"

export type VaccineHistoryCollectionStatus =
    | "collected"
    | "not_collected"

export type VaccineAppointmentCreatedFrom =
    | "patient"
    | "clinic"

export type VaccineHistoryCollectedFrom =
    | "patient"
    | "clinic"

export interface VaccineAppointmentDetails {
    ruleSetId: string
    clinicId: string
    menuId: string
    dobForEvaluation: string
    selectedVaccine: string[]

    scheduledSeasonalDoses?: ScheduledSeasonalVaccineDose[]

    // appointments created together in multi-booking share this id (missing/null means it was created alone)
    multiDoseBookingGroupId?: string | null
    // ordering within group
    multiDoseBookingSequence?: number | null

    doseHistory: VaccineDoseHistoryInput[]
    otherVaccinationHistory?: OtherVaccinationHistoryInput[]
    menuSettingSnapshot?: VaccineMenuSettingSnapshot

    historyCollectionStatus?: VaccineHistoryCollectionStatus
    createdFrom?: VaccineAppointmentCreatedFrom
    historyCollectedFrom?: VaccineHistoryCollectedFrom

    createdAt?: unknown
}

export function resolveVaccineHistoryCollectionStatus(details: VaccineAppointmentDetails): VaccineHistoryCollectionStatus {
    if (details.historyCollectionStatus === "collected" || details.historyCollectionStatus === "not_collected") {
        return details.historyCollectionStatus
    }
    
    const selectedVaccines = Array.isArray(details.selectedVaccine) ? details.selectedVaccine : []
    const doseHistory = Array.isArray(details.doseHistory) ? details.doseHistory : []

    if (selectedVaccines.length === 0) {
        return "not_collected"
    }

    const hasCollectedHistory = selectedVaccines.every(code => {
        const matchingHistory = doseHistory.filter(history => history.code === code)
        if (matchingHistory.length !== 1) {
            return false
        }
        const count = matchingHistory[0].count
        return (count !== null && count !== undefined && count !== "")
    })

    return hasCollectedHistory ? "collected" : "not_collected"
}

export async function readVaccineMenuSetting(menuId: string): Promise<VaccineMenuSetting> {
    return liteGetDocument(liteCollections.vaccineMenuSettings, menuId).then(VaccineMenuSetting.fromDoc)
}

export async function readVaccineAppointmentDetails(appointmentId: string): Promise<VaccineAppointmentDetails> {
    const snapshot = await liteGetDocument(liteCollections.vaccineAppointmentDetails, appointmentId)

    if (!snapshot.exists) {
        throw new Error("vaccine-appointment/details-not-found")
    }

    const data = snapshot.data()

    if (!data) {
        throw new Error("vaccine-appointment/details-not-found")
    }

    return data as VaccineAppointmentDetails
}

export interface GroupedVaccineAppointmentDetails {
    appointmentId: string
    details: VaccineAppointmentDetails
}

export async function readVaccineAppointmentDetailsByMultiDoseBookingGroupId(multiDoseBookingGroupId: string): Promise<GroupedVaccineAppointmentDetails[]> {
    if (!multiDoseBookingGroupId) {
        return []
    }

    const snapshots = await liteQueryCollectionWith(liteCollections.vaccineAppointmentDetails, ["multiDoseBookingGroupId", "==", multiDoseBookingGroupId])
    return snapshots.map(snapshot => {
        const data = snapshot.data()
        if (!data) {
            throw new Error("vaccine-appointment/details-not-found")
        }
        return {
            appointmentId: snapshot.id,
            details: data as VaccineAppointmentDetails
        }
    })
}